import { DefaultButton } from '@fluentui/react/lib/Button';
import * as React from 'react';
import { getStore } from '../store/store';
import { ControlIcons } from 'owa-control-icons';
import { getFilterButtonStyles, getIconStyles } from './SearchFilterStyles';
import { onTimeFilterButtonClicked, onTimeCalloutDismiss } from '../actions/internalActions';
import { Callout } from '@fluentui/react/lib/Callout';
import { Icon } from '@fluentui/react/lib/Icon';
import { observer } from 'mobx-react-lite';
import getTimeFilterDropdownValue from '../selectors/getTimeFilterDropdownValue';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import onTimeRangeSelected from '../utils/onTimeRangeSelected';
import {
    customTime,
    timeFilterText,
    dateDropdownPlaceholder,
    todayDropdownOption,
    yesterdayDropdownOption,
    thisWeekDropdownOption,
    lastWeekDropdownOption,
    thisMonthDropdownOption,
    lastMonthDropdownOption,
    thisYearDropdownOption,
    lastYearDropdownOption,
} from './TimeSearchFilter.locstring.json';
import loc from 'owa-localize';

const styles = require('./TimeSearchFilter.scss');

export default observer(function TimeSearchFilter() {
    const timeDropdownOptions: IDropdownOption[] = [
        { key: 'today', text: loc(todayDropdownOption) },
        { key: 'yesterday', text: loc(yesterdayDropdownOption) },
        { key: 'thisweek', text: loc(thisWeekDropdownOption) },
        { key: 'lastweek', text: loc(lastWeekDropdownOption) },
        { key: 'thismonth', text: loc(thisMonthDropdownOption) },
        { key: 'lastmonth', text: loc(lastMonthDropdownOption) },
        { key: 'thisyear', text: loc(thisYearDropdownOption) },
        { key: 'lastyear', text: loc(lastYearDropdownOption) },
    ];

    const dropdownValue = getTimeFilterDropdownValue();
    const buttonRef = React.useRef<HTMLDivElement>();
    let buttonDisplayText;
    if (dropdownValue === 'custom') {
        // If user has selected a time range that doesn't map to one of the dropdown ranges, show 'Custom'
        // Add 'from any date' option so user can clear an already selected dropdown option
        buttonDisplayText = loc(customTime);
        timeDropdownOptions.unshift({ key: 'anydate', text: loc(dateDropdownPlaceholder) });
    } else if (dropdownValue) {
        // If the user has selected a date range that maps to one of the options in the dropdown, use the dropdown option as the display text
        // Add 'from any date' option so user can clear an already selected dropdown option
        buttonDisplayText = timeDropdownOptions.filter(option => option.key === dropdownValue)[0]
            .text;
        timeDropdownOptions.unshift({ key: 'anydate', text: loc(dateDropdownPlaceholder) });
    } else {
        // Otherwise show 'Time' placeholder value
        buttonDisplayText = loc(timeFilterText);
    }

    const onDropdownSelect = React.useCallback(
        (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
            if (item.key !== dropdownValue) {
                onTimeRangeSelected(item);
            }
        },
        [dropdownValue]
    );

    return (
        <div ref={buttonRef}>
            <DefaultButton
                toggle={true}
                checked={dropdownValue !== null}
                text={buttonDisplayText}
                iconProps={{
                    iconName: ControlIcons.Clock,
                    styles: getIconStyles(),
                }}
                styles={getFilterButtonStyles()}
                onClick={onTimeFilterButtonClicked}>
                <Icon
                    iconName={ControlIcons.ChevronDownMed}
                    styles={{ root: { paddingLeft: '4px', paddingRight: '4px' } }}
                />
            </DefaultButton>
            {getStore().shouldShowTimeFilterCallout && (
                <Callout
                    target={buttonRef}
                    isBeakVisible={false}
                    onDismiss={onTimeCalloutDismiss}
                    styles={{ calloutMain: { padding: '12px 48px 12px 8px' } }}>
                    <div className={styles.calloutHeader}>
                        <Icon
                            iconName={ControlIcons.Clock}
                            styles={{
                                root: {
                                    height: '16px',
                                    width: '16px',
                                    fontSize: '16px',
                                    paddingRight: '8px',
                                },
                            }}
                        />
                        <span className={styles.calloutHeaderText}>{loc(timeFilterText)}</span>
                    </div>
                    <Dropdown
                        placeholder={
                            dropdownValue === 'custom'
                                ? loc(customTime)
                                : loc(dateDropdownPlaceholder)
                        }
                        selectedKey={dropdownValue}
                        options={timeDropdownOptions}
                        onChange={onDropdownSelect}
                        styles={{
                            root: {
                                paddingLeft: '22px',
                            },
                            dropdown: {
                                minWidth: '200px',
                            },
                        }}
                    />
                </Callout>
            )}
        </div>
    );
});
